import { arrayMove } from "@dnd-kit/sortable";
import { times, zip } from "lodash-es";
import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { v4 } from "uuid";

import { ErrorBaseInfo, FieldErrorsBE } from "src/api/types";
import {
  DecisionTableOperators,
  MatrixTableEffect,
  MatrixTablePredicate,
} from "src/clients/flow-api";
import {
  MatrixTableError,
  MatrixTableNodeForm,
} from "src/matrixTableNode/MatrixTableNodeEditor";

export const buildMatrixTableActions = ({
  getValues,
  setValue,
}: {
  getValues: UseFormGetValues<MatrixTableNodeForm>;
  setValue: UseFormSetValue<MatrixTableNodeForm>;
}) => {
  return {
    getColumnPredicate: (id: string) => {
      const columnPredicates = getValues("column_predicates");
      const index = columnPredicates.findIndex((p) => p.id === id);
      return getValues(`column_predicates.${index}`);
    },
    updateColumnPredicate: (
      index: number,
      predicateValue: MatrixTablePredicate["value"],
      predicateOperator: MatrixTablePredicate["operator"],
    ) => {
      setValue(`column_predicates.${index}.value`, predicateValue);
      setValue(`column_predicates.${index}.operator`, predicateOperator);
    },
    updateRowPredicate: (
      index: number,
      predicateValue: MatrixTablePredicate["value"],
      predicateOperator: MatrixTablePredicate["operator"],
    ) => {
      setValue(`row_predicates.${index}.value`, predicateValue);
      setValue(`row_predicates.${index}.operator`, predicateOperator);
    },
    updateEffect: (rowIndex: number, colIndex: number, effectValue: string) => {
      setValue(`grid_effects.${rowIndex}.${colIndex}.value`, effectValue);
    },
    insertRow: (rowIndex: number, duplicate: boolean = false) => {
      const rowPredicates = getValues("row_predicates");
      const columnPredicates = getValues("column_predicates");
      const gridEffects = getValues("grid_effects");

      const newRowEffects: MatrixTableEffect[] = times(
        columnPredicates.length,
        (colIndex) => ({
          id: v4(),
          value: duplicate ? gridEffects[rowIndex][colIndex].value : "",
        }),
      );

      if (duplicate) {
        const newRowPredicate = {
          id: v4(),
          value: rowPredicates[rowIndex].value,
          operator: rowPredicates[rowIndex].operator,
        };
        setValue("grid_effects", [
          ...gridEffects.slice(0, rowIndex + 1),
          newRowEffects,
          ...gridEffects.slice(rowIndex + 1),
        ]);
        setValue("row_predicates", [
          ...rowPredicates.slice(0, rowIndex + 1),
          newRowPredicate,
          ...rowPredicates.slice(rowIndex + 1),
        ]);
      } else {
        const newRowPredicate = {
          id: v4(),
          value: "",
          operator: DecisionTableOperators.EQ,
        };
        setValue("grid_effects", [
          ...gridEffects.slice(0, rowIndex),
          newRowEffects,
          ...gridEffects.slice(rowIndex),
        ]);
        setValue("row_predicates", [
          ...rowPredicates.slice(0, rowIndex),
          newRowPredicate,
          ...rowPredicates.slice(rowIndex),
        ]);
      }
    },
    insertColumn: (columnIndex: number, duplicate: boolean = false) => {
      const columnPredicates = getValues("column_predicates");
      const gridEffects = getValues("grid_effects");
      const newColumnPredicate = {
        id: v4(),
        value: duplicate ? columnPredicates[columnIndex].value : "",
        operator:
          columnPredicates[columnIndex]?.operator ?? DecisionTableOperators.EQ,
      };

      const sliceIndex = duplicate ? columnIndex + 1 : columnIndex;

      const newGridEffects = gridEffects.map((rowEffects) => {
        return [
          ...rowEffects.slice(0, sliceIndex),
          { id: v4(), value: duplicate ? rowEffects[columnIndex].value : "" },
          ...rowEffects.slice(sliceIndex),
        ];
      });

      const newColumnPredicates = [
        ...columnPredicates.slice(0, sliceIndex),
        newColumnPredicate,
        ...columnPredicates.slice(sliceIndex),
      ];

      setValue("grid_effects", newGridEffects);
      setValue("column_predicates", newColumnPredicates);
    },
    deleteRow: (index: number) => {
      const rowPredicates = getValues("row_predicates");
      const gridEffects = getValues("grid_effects");
      const rowPredicate = rowPredicates[index];

      setValue(
        "grid_effects",
        gridEffects.filter((_, i) => i !== index),
      );

      setValue(
        "row_predicates",
        rowPredicates.filter(({ id }) => id !== rowPredicate.id),
      );
    },
    deleteColumn: (index: number) => {
      const columnPredicates = getValues("column_predicates");
      const gridEffects = getValues("grid_effects");
      const columnPredicate = columnPredicates[index];

      setValue(
        "grid_effects",
        gridEffects.map((rowEffects) =>
          rowEffects.filter((_, i) => i !== index),
        ),
      );
      setValue(
        "column_predicates",
        columnPredicates.filter(({ id }) => id !== columnPredicate.id),
      );
    },
    reorderRowPredicates: (oldIndex: number, newIndex: number) => {
      const rowPredicates = getValues("row_predicates");
      const gridEffects = getValues("grid_effects");

      const newRowPredicates = arrayMove(rowPredicates, oldIndex, newIndex);
      const newGridEffects = arrayMove(gridEffects, oldIndex, newIndex);

      setValue("grid_effects", newGridEffects);
      setValue("row_predicates", newRowPredicates);
    },
  };
};

export type MatrixTableActions = ReturnType<typeof buildMatrixTableActions>;

const getFieldMessageError = (errorLookup: FieldErrorsBE, fieldId: string) =>
  errorLookup.find((error) => error.field_id === fieldId)?.message;

export const getErrors = (
  displayedError: ErrorBaseInfo | undefined,
  form: MatrixTableNodeForm,
): {
  errors: MatrixTableError[];
  fallbackErrorMessage?: string;
  rowFieldErrorMessage?: string;
  columnFieldErrorMessage?: string;
  resultFieldErrorMessage?: string;
} => {
  if (!displayedError) {
    return {
      errors: [],
      fallbackErrorMessage: undefined,
      rowFieldErrorMessage: undefined,
      columnFieldErrorMessage: undefined,
      resultFieldErrorMessage: undefined,
    };
  }
  const errorLookup = [...(displayedError.field_errors ?? [])];

  if (displayedError.loc && typeof displayedError.msg === "string") {
    errorLookup.push({
      field_id: displayedError.loc as string,
      message: displayedError.msg,
      field_name: "",
    });
  }

  const errors: MatrixTableError[] = [];

  const fallbackErrorMessage = getFieldMessageError(
    errorLookup,
    form.fallback.id,
  );
  const rowFieldErrorMessage = getFieldMessageError(
    errorLookup,
    form.row_field.id,
  );
  const columnFieldErrorMessage = getFieldMessageError(
    errorLookup,
    form.column_field.id,
  );
  const resultFieldErrorMessage = getFieldMessageError(
    errorLookup,
    form.result_field.id,
  );

  form.row_predicates.forEach((rowPredicate, index) => {
    const message = getFieldMessageError(errorLookup, rowPredicate.id);
    if (message) {
      errors.push({
        row: index,
        col: 0,
        message,
      });
    }
  });

  form.column_predicates.forEach((columnPredicate, index) => {
    const message = getFieldMessageError(errorLookup, columnPredicate.id);
    if (message) {
      errors.push({
        row: -1,
        col: index + 1, // +1 because the first column is the row predicate
        message,
      });
    }
  });

  form.grid_effects.forEach((rowEffects, row) => {
    rowEffects.forEach((effect, col) => {
      const message = getFieldMessageError(errorLookup, effect.id);
      if (message) {
        errors.push({
          row,
          col: col + 1, // +1 because the first column is the row predicate
          message,
        });
      }
    });
  });

  return {
    errors,
    fallbackErrorMessage,
    rowFieldErrorMessage,
    columnFieldErrorMessage,
    resultFieldErrorMessage,
  };
};

export const isMatrixNodeInDefaultState = (form: MatrixTableNodeForm) => {
  return (
    form.row_field.value === "" &&
    form.column_field.value === "" &&
    form.result_field.value === "" &&
    form.fallback.value === "None" &&
    form.row_predicates.every(
      (predicate) =>
        predicate.value === "" &&
        predicate.operator === DecisionTableOperators.EQ,
    ) &&
    form.column_predicates.every(
      (predicate) =>
        predicate.value === "" &&
        predicate.operator === DecisionTableOperators.EQ,
    ) &&
    form.grid_effects.every((row) => row.every((effect) => effect.value === ""))
  );
};

export const swapConditions = (form: MatrixTableNodeForm) => {
  const {
    row_field,
    column_field,
    row_predicates,
    column_predicates,
    ...rest
  } = form;
  return {
    ...rest,
    row_field: column_field,
    column_field: row_field,
    row_predicates: column_predicates,
    column_predicates: row_predicates,
    grid_effects: zip(...form.grid_effects) as unknown as MatrixTableEffect[][],
  };
};

export const isFloatingOperatorDropdown = (
  e: MouseEvent | TouchEvent | FocusEvent,
) => {
  return (
    e.target instanceof HTMLElement &&
    e.target.closest("[data-loc='floating-operator-dropdown-dropdown']")
  );
};
